import { Container, Graphics, Text } from 'pixi.js';
import { COLORS, textStyle, drawPanel } from './theme';

const PAD = 8;
const SWATCH = 14;
const BAR_H = 13;
// Wide (desktop) vs compact (portrait phone) so the panel never collides with
// the status panel / wind dial across the narrow top strip.
const LAYOUT = {
  wide: { panelW: 286, rowH: 24, barW: 78, showValue: true },
  compact: { panelW: 200, rowH: 22, barW: 52, showValue: false },
} as const;

export interface TeamHealthData {
  /** Unique, stable key (the team id) - names can repeat when one player
   *  claims several teams, so rows must NOT be keyed by the display name. */
  id: string;
  name: string;
  color: number;
  totalHealth: number;
  maxHealth: number;
  /** Shared alliance accent colour (undefined when there are no alliances). */
  allianceColor?: number;
}

interface Row {
  accent: Graphics;
  swatch: Graphics;
  label: Text;
  barBg: Graphics;
  barFill: Graphics;
  value: Text;
}

/**
 * Top-right team summary (Fusion chrome): a beveled panel listing each team
 * with a glossy colour swatch, name, an HP bar and the live HP value. In
 * delay/turn-order modes the name carries the delay suffix from the caller.
 * Updated every frame, so rows are created lazily and only mutated after.
 */
export class TeamHealthSummary {
  container: Container;
  private panel: Graphics;
  private rows: Map<string, Row> = new Map();
  private lastRowCount = -1;
  private compact = false;
  private lastCompact: boolean | null = null;

  constructor() {
    this.container = new Container();
    this.panel = new Graphics();
    this.container.addChild(this.panel);
  }

  /** Switch between the wide (desktop) and compact (portrait) layouts. */
  setCompact(compact: boolean): void {
    this.compact = compact;
  }

  update(teams: TeamHealthData[]): void {
    const seen = new Set<string>();
    const L = this.compact ? LAYOUT.compact : LAYOUT.wide;

    if (teams.length !== this.lastRowCount || this.compact !== this.lastCompact) {
      this.lastRowCount = teams.length;
      this.lastCompact = this.compact;
      const h = teams.length * L.rowH + PAD * 2;
      this.panel.clear();
      drawPanel(this.panel, 0, 0, L.panelW, h, { radius: 12, alpha: 0.72 });
    }

    teams.forEach((team, index) => {
      const y = PAD + index * L.rowH + L.rowH / 2;
      seen.add(team.id);

      let row = this.rows.get(team.id);
      if (!row) {
        row = this.createRow(team.color);
        this.rows.set(team.id, row);
      }

      // Alliance accent: a thin coloured bar at the row's left edge so each
      // team's side is readable at a glance during the match.
      row.accent.clear();
      if (team.allianceColor !== undefined) {
        row.accent
          .roundRect(2, y - L.rowH / 2 + 3, 3, L.rowH - 6, 1.5)
          .fill({ color: team.allianceColor });
      }

      // Glossy swatch
      row.swatch.position.set(PAD, y - SWATCH / 2);

      // Name
      row.label.text = team.name;
      row.label.position.set(PAD + SWATCH + 8, y);

      // Bar
      const barX = L.panelW - L.barW - PAD;
      const barY = y - BAR_H / 2;
      row.barBg.position.set(barX, barY);
      row.barBg.scale.x = L.barW / LAYOUT.wide.barW;
      const pct =
        team.maxHealth > 0 ? Math.max(0, Math.min(1, team.totalHealth / team.maxHealth)) : 0;
      row.barFill.clear();
      // Health colour shifts green -> amber -> red as the team is worn down.
      const hpColor = pct > 0.5 ? COLORS.go : pct > 0.25 ? COLORS.gold : COLORS.danger;
      row.barFill
        .roundRect(barX + 1, barY + 1, (L.barW - 2) * pct, BAR_H - 2, 3)
        .fill({ color: hpColor });

      row.value.visible = L.showValue;
      row.value.text = `${Math.max(0, Math.round(team.totalHealth))}`;
      row.value.position.set(barX + L.barW / 2, y);
    });

    for (const [id, row] of this.rows) {
      if (!seen.has(id)) {
        row.accent.destroy();
        row.swatch.destroy();
        row.label.destroy();
        row.barBg.destroy();
        row.barFill.destroy();
        row.value.destroy();
        this.rows.delete(id);
      }
    }
  }

  private createRow(color: number): Row {
    const accent = new Graphics();
    this.container.addChild(accent);

    const swatch = new Graphics();
    swatch.circle(SWATCH / 2, SWATCH / 2, SWATCH / 2).fill({ color });
    swatch
      .circle(SWATCH / 2, SWATCH / 2 - 1, SWATCH / 2 - 2)
      .fill({ color: COLORS.white, alpha: 0.25 });
    swatch
      .circle(SWATCH / 2, SWATCH / 2, SWATCH / 2)
      .stroke({ width: 1.5, color: 0xffffff, alpha: 0.7 });
    this.container.addChild(swatch);

    const label = new Text({
      text: '',
      style: textStyle({ size: 13, weight: '700', shadow: false }),
    });
    label.anchor.set(0, 0.5);
    this.container.addChild(label);

    const barBg = new Graphics();
    barBg.roundRect(0, 0, LAYOUT.wide.barW, BAR_H, 4).fill({ color: 0x05101f, alpha: 0.85 });
    barBg.roundRect(0, 0, LAYOUT.wide.barW, BAR_H, 4).stroke({ width: 1, color: COLORS.stroke });
    this.container.addChild(barBg);

    const barFill = new Graphics();
    this.container.addChild(barFill);

    const value = new Text({ text: '', style: textStyle({ size: 11, weight: '700', outline: 3 }) });
    value.anchor.set(0.5, 0.5);
    this.container.addChild(value);

    return { accent, swatch, label, barBg, barFill, value };
  }

  /** Current rendered panel width (for safe-zone aware positioning). */
  get width(): number {
    return this.compact ? LAYOUT.compact.panelW : LAYOUT.wide.panelW;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
